import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Thermometer, Trash2, Edit, Power, PowerOff, GripVertical, MoreVertical } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TagesgeschaeftShareBar } from '../../components/TagesgeschaeftShareBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Item Component for Drag & Drop with Mobile Optimization
const SortableItem = ({ point, locations, onEdit, onToggleStatus, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id: point.id });

  const [showActions, setShowActions] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : 'Unbekannt';
  };

  const isActive = point.status === 'active';

  // Single drag handle component to avoid duplicate refs
  const DragHandle = () => (
    <div
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing hover:bg-gray-200 rounded touch-none flex-shrink-0"
      style={{ width: '40px', height: '40px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      aria-label="Messpunkt verschieben"
    >
      <GripVertical className="h-5 w-5 text-gray-400" />
    </div>
  );

  return (
    <div ref={setNodeRef} style={style}>
      <Card className={`${!isActive ? 'opacity-60 bg-gray-50' : ''} ${isDragging ? 'shadow-lg bg-orange-50' : ''}`}>
        <CardHeader className="pb-3">
          {/* Mobile Layout (< 768px) */}
          <div className="md:hidden">
            {/* Row 1: Drag Handle, Icon, Name, 3-Dot Menu */}
            <div className="flex items-start gap-3 mb-3">
              <DragHandle />
              <div className={`p-2 rounded-md flex-shrink-0 ${isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Thermometer className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base leading-tight">{point.name}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  📍 {getLocationName(point.location_id)}
                </p>
              </div>
              {/* 3-Dot Menu Button */}
              <button
                type="button"
                onClick={() => setShowActions(!showActions)}
                className="p-2 hover:bg-gray-200 rounded-full flex-shrink-0"
              >
                <MoreVertical className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Row 2: Temperature Info */}
            <div className="space-y-1.5 text-sm mb-3">
              <p>
                <strong>🎯 Soll: {point.target_temp}°C</strong>
                <span className="text-muted-foreground ml-2">(±{point.tolerance}°C)</span>
              </p>
              <p className="text-xs text-muted-foreground">
                📊 Bereich: {point.target_temp - point.tolerance}°C bis {point.target_temp + point.tolerance}°C
              </p>
              {point.hint && (
                <p className="text-xs text-blue-600 italic mt-2">💡 {point.hint}</p>
              )}
            </div>

            {/* Row 3: Large Status Toggle */}
            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-md mb-2">
              <span className="text-sm font-medium">Status</span>
              <button
                onClick={() => onToggleStatus(point)}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-all ${
                  isActive 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                }`}
              >
                {isActive ? '🟢 Aktiv' : '⚫ Inaktiv'}
              </button>
            </div>

            {/* Row 4: Collapsible Actions Menu */}
            {showActions && (
              <div className="flex gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onEdit(point);
                    setShowActions(false);
                  }}
                  className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Bearbeiten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onDelete(point.id);
                    setShowActions(false);
                  }}
                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </Button>
              </div>
            )}
          </div>

          {/* Desktop Layout (>= 768px) - Original Design */}
          <div className="hidden md:block">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <DragHandle />
                <div className={`p-3 rounded-md ${isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Thermometer className={`h-6 w-6 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <CardTitle className="text-xl">{point.name}</CardTitle>
                  <CardDescription>
                    <div className="mt-2 space-y-1">
                      <p>Standort: <strong>{getLocationName(point.location_id)}</strong></p>
                      <p className="text-sm">
                        <strong>Soll: {point.target_temp}°C (±{point.tolerance}°C)</strong>
                        <span className="text-muted-foreground ml-2">
                          Bereich: {point.target_temp - point.tolerance}°C bis {point.target_temp + point.tolerance}°C
                        </span>
                      </p>
                      {point.hint && (
                        <p className="text-sm text-muted-foreground italic">💡 {point.hint}</p>
                      )}
                      <div className="mt-2">
                        <Badge variant={isActive ? 'default' : 'secondary'}>
                          {isActive ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                    </div>
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onToggleStatus(point)}
                  title={isActive ? 'Deaktivieren' : 'Aktivieren'}
                >
                  {isActive ? (
                    <PowerOff className="h-4 w-4 text-orange-500" />
                  ) : (
                    <Power className="h-4 w-4 text-green-500" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(point)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(point.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );
};

export const TemperatureSettings = () => {
  const [measuringPoints, setMeasuringPoints] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location_id: '',
    target_temp: 0,
    tolerance: 3,
    hint: '',
    order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pointsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/admin/temperature/measuring-points`),
        axios.get(`${API}/admin/locations`)
      ]);
      // Sort by order
      const sorted = pointsRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      setMeasuringPoints(sorted);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/temperature/measuring-points`, {
        ...formData,
        target_temp: parseFloat(formData.target_temp),
        tolerance: parseFloat(formData.tolerance),
        order: parseInt(formData.order) || measuringPoints.length + 1
      });
      toast.success('Messpunkt erfolgreich hinzugefügt!');
      setDialogOpen(false);
      setFormData({ name: '', location_id: '', target_temp: 0, tolerance: 3, hint: '', order: 0 });
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Hinzufügen');
    }
  };

  const handleEdit = (point) => {
    setEditingPoint({ ...point });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/temperature/measuring-points/${editingPoint.id}`, {
        name: editingPoint.name,
        target_temp: parseFloat(editingPoint.target_temp),
        tolerance: parseFloat(editingPoint.tolerance),
        hint: editingPoint.hint,
        order: parseInt(editingPoint.order) || 0
      });
      toast.success('Messpunkt aktualisiert');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleToggleStatus = async (point) => {
    try {
      const newStatus = point.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${API}/admin/temperature/measuring-points/${point.id}`, {
        status: newStatus
      });
      toast.success(newStatus === 'active' ? 'Messpunkt aktiviert' : 'Messpunkt deaktiviert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Messpunkt wirklich löschen? Bei vorhandenen Messungen wird er nur deaktiviert.')) return;
    
    try {
      const response = await axios.delete(`${API}/admin/temperature/measuring-points/${id}`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Löschen');
    }
  };

  // DnD Kit Sensors for mouse and touch support
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = measuringPoints.findIndex((p) => p.id === active.id);
      const newIndex = measuringPoints.findIndex((p) => p.id === over.id);
      
      const newOrder = arrayMove(measuringPoints, oldIndex, newIndex);
      setMeasuringPoints(newOrder);
      
      // Update order in backend
      try {
        await Promise.all(
          newOrder.map((point, index) =>
            axios.put(`${API}/admin/temperature/measuring-points/${point.id}`, { order: index })
          )
        );
        toast.success('Reihenfolge gespeichert');
      } catch (error) {
        toast.error('Fehler beim Speichern der Reihenfolge');
        fetchData(); // Revert on error
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="temperature-settings-title">
              Temperatur-Messpunkte
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">Verwalten Sie Ihre Kühlschränke, Truhen und Kühlräume</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-measuring-point-button" className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Messpunkt hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Neuen Messpunkt anlegen</DialogTitle>
                  <DialogDescription>
                    Fügen Sie einen Kühlschrank, eine Truhe oder einen Kühlraum hinzu.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name des Messpunkts *</Label>
                    <Input
                      id="name"
                      placeholder="z.B. Tiefkühltruhe, Kühlschrank Getränke"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Standort *</Label>
                    <select
                      id="location"
                      value={formData.location_id}
                      onChange={(e) => handleChange('location_id', e.target.value)}
                      required
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="">Standort auswählen</option>
                      {locations.map(location => (
                        <option key={location.id} value={location.id}>{location.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="target_temp">Soll-Temperatur (°C) *</Label>
                      <Input
                        id="target_temp"
                        type="number"
                        step="0.5"
                        placeholder="-18"
                        value={formData.target_temp}
                        onChange={(e) => handleChange('target_temp', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tolerance">Toleranz (±°C) *</Label>
                      <Input
                        id="tolerance"
                        type="number"
                        step="0.5"
                        min="0.5"
                        placeholder="3"
                        value={formData.tolerance}
                        onChange={(e) => handleChange('tolerance', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hint">Hinweistext (optional)</Label>
                    <Input
                      id="hint"
                      placeholder="z.B. Täglich vor 10 Uhr messen"
                      value={formData.hint}
                      onChange={(e) => handleChange('hint', e.target.value)}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit">
                    Messpunkt anlegen
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {locations.length > 0 && (
          <TagesgeschaeftShareBar
            contentType="temperatur"
            listLabel="Temperatur-Messpunkte"
            locations={locations}
            onSent={fetchData}
          />
        )}

        {locations.length === 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Sie haben noch keine Standorte angelegt. Bitte erstellen Sie zuerst einen Standort unter "Standorte".
              </p>
            </CardContent>
          </Card>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={measuringPoints.map(p => p.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid gap-4">
              {measuringPoints.map((point) => (
                <SortableItem
                  key={point.id}
                  point={point}
                  locations={locations}
                  onEdit={handleEdit}
                  onToggleStatus={handleToggleStatus}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {measuringPoints.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Thermometer className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Noch keine Messpunkte angelegt</p>
              <p className="text-sm text-muted-foreground mt-2">
                Klicken Sie auf "Messpunkt hinzufügen" um zu beginnen
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tipp:</strong> Ziehen Sie die Messpunkte mit dem Greif-Symbol (⠿) um die Reihenfolge zu ändern.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Messpunkt bearbeiten</DialogTitle>
          </DialogHeader>
          {editingPoint && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingPoint.name}
                  onChange={(e) => setEditingPoint({...editingPoint, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Soll-Temperatur (°C)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editingPoint.target_temp}
                    onChange={(e) => setEditingPoint({...editingPoint, target_temp: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Toleranz (±°C)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={editingPoint.tolerance}
                    onChange={(e) => setEditingPoint({...editingPoint, tolerance: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Hinweistext (optional)</Label>
                <Input
                  value={editingPoint.hint || ''}
                  onChange={(e) => setEditingPoint({...editingPoint, hint: e.target.value})}
                  placeholder="z.B. Täglich vor 10 Uhr messen"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEdit}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
